import React, { useState, useEffect } from "react";
import FileUpload from "../../Components/FIleUpload/FileUpload";
import FileList from "../../Components/fileList/FIleList";
import CreateOrganizationDialog from "../../Components/CreateOrganizationDialog/CreateOrganizationDialog";
import ManageInvitations from "../../Components/ManageInvitations/ManageInvitations";
import { createOrganization, uploadFiles, fetchUserOrganization, fetchOrganizationFiles, fetchUserOrganizationRole, deleteOrganization, isUserSubscribed } from "../../Utils/functions";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import LinearProgress from "@mui/material/LinearProgress";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "./OrgDatasetPage.css";
import { useNavigate } from "react-router-dom";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
const OrgDatasetPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthenticator((context) => [context.user]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);
  const [deleteConfirmationDialogOpen, setDeleteConfirmationDialogOpen] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [currentTab, setCurrentTab] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    // check if user has subscription
    isUserSubscribed().then((data) => {
      if (!data.isActive) {
        setSnackbar({
          open: true,
          message: "You need to upgrade your subscription to access organization features.",
          severity: "warning",
        });
        navigate("/login");
      }
    }).catch((error) => {
      console.error("Error checking subscription status:", error);
      setSnackbar({
        open: true,
        message: "Failed to check subscription status. Please try again later.",
        severity: "error",
      });
    });
  }, [user]);

  useEffect(() => {
    const checkOrgAndFiles = async () => {
      const orgId = localStorage.getItem("organizationId");
      const orgNameStored = localStorage.getItem("organizationName");
      let orgIdToUse = orgId;
      if (orgId && orgNameStored) {
        setOrgName(orgNameStored);
      } else {
        try {
          const org = await fetchUserOrganization();
          if (org && org.id) {
            localStorage.setItem("organizationId", org.id);
            if (org.name) localStorage.setItem("organizationName", org.name);
            setOrgName(org.name || "");
            orgIdToUse = org.id;
          } else {
            setOrgDialogOpen(true);
            return;
          }
        } catch (err) {
          setOrgDialogOpen(true);
          return;
        }
      }
      // Fetch files if orgId is available
      if (orgIdToUse) {
        try {
          const files = await fetchOrganizationFiles(orgIdToUse);
          setUploadedFiles(files || []);
          // Check if user is admin
          const userRole = await fetchUserOrganizationRole(orgIdToUse);
          setIsAdmin(userRole === "ADMIN");
        } catch (err) {
          setUploadedFiles([]);
        }
      }
    };
    checkOrgAndFiles();
  }, [user]);

  const handleFilesChange = async (files) => {
    const orgId = localStorage.getItem("organizationId");
    if (!orgId || files.length === 0) return;
    setUploading(true);
    try {
      await uploadFiles(orgId, files);
      setSnackbar({ open: true, message: "Files uploaded successfully!", severity: "success" });
      const updatedFiles = await fetchOrganizationFiles(orgId);
      setUploadedFiles(updatedFiles || []);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to upload files. Please try again.", severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleCreateOrg = async (name) => {
    try {
      const data = await createOrganization(name);
      if (data && data.organization) {
        localStorage.setItem("organizationId", data.organization.id);
        localStorage.setItem("organizationName", data.organization.name);
        setOrgName(name);
        setOrgDialogOpen(false);
        setSnackbar({ open: true, message: "Organization created successfully!", severity: "success" });
        const files = await fetchOrganizationFiles(data.organization.id);
        setUploadedFiles(files || []);
        setIsAdmin(true); // Assuming the creator is an admin
        setCurrentTab(0); // Switch to Manage Users tab
      } else {
        setSnackbar({ open: true, message: "Failed to create organization. Please try again.", severity: "error" });
      }
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to create organization. Please try again.", severity: "error" });
    }
  };

  const handleRefreshFiles = async () => {
    const orgId = localStorage.getItem("organizationId");
    if (!orgId) return;
    try {
      const files = await fetchOrganizationFiles(orgId);
      setUploadedFiles(files || []);
    } catch (err) {
      setUploadedFiles([]);
    }
  };

  const handleDialogClose = () => {
    setOrgDialogOpen(false);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleDeleteOrganization = async () => {
    const orgId = localStorage.getItem("organizationId");
    if (!orgId) return;
    try {
      await deleteOrganization(orgId);
      localStorage.removeItem("organizationId");
      localStorage.removeItem("organizationName");
      setOrgName("");
      setUploadedFiles([]);
      setSnackbar({ open: true, message: "Organization deleted successfully!", severity: "success" });
      setDeleteConfirmationDialogOpen(false);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to delete organization. Please try again.", severity: "error" });
    }
  };

  return (
    <div className="org-dataset-page">
      <CreateOrganizationDialog
        open={orgDialogOpen}
        onClose={handleDialogClose}
        onCreate={handleCreateOrg}
      />
      <Dialog
        open={deleteConfirmationDialogOpen}
        onClose={() => setDeleteConfirmationDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{"Delete Organization?"}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this organization? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmationDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteOrganization} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <h2 className="org-dataset-title">
        {orgName ? `${orgName} Dataset` : "Organization Dataset"}
      </h2>

      {orgName && (
        <Box sx={{ width: "100%", padding: 1 }}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", padding: 1 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              aria-label="organization tabs"
            >
              {isAdmin && <Tab label="Manage Users" {...a11yProps(0)} />}
              <Tab label="Manage Files" {...a11yProps(isAdmin ? 1 : 0)} />
              {isAdmin && <Tab label="Settings" {...a11yProps(isAdmin ? 2 : 1)} />}
            </Tabs>
          </Box>
          {isAdmin && (
            <TabPanel value={currentTab} index={0} sx={{ padding: 1 }}>
              <ManageInvitations organizationId={localStorage.getItem("organizationId")} />
            </TabPanel>
          )}
          <TabPanel value={currentTab} index={isAdmin ? 1 : 0}>
           {isAdmin && <FileUpload onUpload={handleFilesChange} isAdmin={isAdmin} />}
            {uploading && (
              <Box sx={{ width: '100%', mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <LinearProgress sx={{ flex: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
                  Uploading files...
                </Typography>
              </Box>
            )}
            <div className="org-dataset-list-section">
              <h3>Uploaded Files</h3>
              <FileList files={uploadedFiles} onRemove={() => {}} onRefresh={handleRefreshFiles} isAdmin={isAdmin} />
            </div>
          </TabPanel>
          {isAdmin && (
            <TabPanel value={currentTab} index={2}>
              <Button variant="contained" color="error" onClick={() => setDeleteConfirmationDialogOpen(true)}>
                Delete Organization
              </Button>
            </TabPanel>
          )}
        </Box>
      )}

      <Snackbar
        open={Boolean(snackbar.open)}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default OrgDatasetPage;
